<template>
  <AppSafeArea :navigation="Boolean(route.meta.tab) && !auth.isTreatmentUnavailable">
    <div
      v-if="app.isLoading"
      class="app-progress tw:fixed tw:top-(--app-safe-top) tw:bg-(--link) tw:h-0.75"
      role="progressbar"
      :aria-label="$t('common.loading')"
    ></div>
    <RouterView v-slot="{ Component }"
      ><!-- Retiring a session must unmount its page, not remount it without credentials. -->
      <component
        v-if="
          !auth.isTreatmentUnavailable &&
          (!route.meta.requiresAuth || auth.isLoggedIn || route.meta.moduleTag === 'auth')
        "
        :is="Component"
        :key="route.fullPath + (auth.account.mainUnique || '')"
    /></RouterView>
    <AppAssistant
      v-if="auth.isLoggedIn && route.meta.tab && !auth.isTreatmentUnavailable"
      :key="auth.authStateVersion"
    />
    <template #navigation
      ><AppBottomBar v-if="!auth.isTreatmentUnavailable" :active="route.meta.tab || APP_TAB.HOME"
    /></template>
  </AppSafeArea>
  <AppToast />
  <AuthTreatmentRequiredDialog v-if="auth.isTreatmentUnavailable" />
  <template v-else>
    <AppConfirmDialog />
    <AppCallSheet />
    <FluidPrompt />
    <NotificationHost />
    <DailyCheckinHost />
  </template>
  <AppStatusDialog @ready="hideLaunchScreen" @startup-error="showLaunchError($t)" />
</template>
<script setup>
// 1. Import necessary components
import { watch, onMounted, onBeforeUnmount, onErrorCaptured } from 'vue';
import Axios from 'axios';
import ApplicationError from '@/shared/services/core/application-error';
import { useRoute, useRouter } from 'vue-router';
import { Capacitor } from '@capacitor/core';
import { App as NativeApp } from '@capacitor/app';
import { APP_TAB } from '@/shared/constants/app';
import { useAuthStore } from '@/core/stores/auth.store';
import { useAppStore } from '@/core/stores/app.store';
import { usePreviewStore } from '@/core/stores/preview.store';
import { useDryWeightUpdateStore } from '@/core/stores/dry-weight-update.store';
import { useDismissKeyboardOnOutsideClick } from '@/shared/composables/useDismissKeyboardOnOutsideClick';
import { useSessionLifecycle } from '@/shared/composables/useSessionLifecycle';
import { DialogService } from '@/shared/utilities/feedback/dialog-service';
import { CallSheetService } from '@/shared/utilities/feedback/call-sheet-service';
import AuthTreatmentRequiredDialog from '@/features/auth/components/AuthTreatmentRequiredDialog.vue';
import AppSafeArea from '@/shared/components/AppSafeArea.vue';
import AppBottomBar from '@/shared/components/AppBottomBar.vue';
import AppAssistant from '@/shared/components/AppAssistant.vue';
import AppToast from '@/shared/components/AppToast.vue';
import AppConfirmDialog from '@/shared/components/AppConfirmDialog.vue';
import AppCallSheet from '@/shared/components/AppCallSheet.vue';
import AppStatusDialog from '@/shared/components/AppStatusDialog.vue';
import FluidPrompt from '@/features/fluid/components/FluidPrompt.vue';
import NotificationHost from '@/features/notifications/components/NotificationHost.vue';
import DailyCheckinHost from '@/features/home/components/DailyCheckinHost.vue';
import { hideLaunchScreen, showLaunchError } from './launch-screen';
// 2. Use
const route = useRoute();
const router = useRouter();
const auth = useAuthStore();
const app = useAppStore();
const preview = usePreviewStore();
const dryWeightUpdates = useDryWeightUpdateStore();
useDismissKeyboardOnOutsideClick();
useSessionLifecycle();
// 3. Define a reactive variable
let backListener;
let mounted = true;
// 4. Define methods or computed properties if needed
// 5. VueJS Hooks
onErrorCaptured((error) => {
  // RestClient owns request feedback. Keep rejected operations rejected, without
  // repeating transport-specific catches in pages; programming errors still propagate.
  if (Axios.isCancel(error) || Axios.isAxiosError(error) || error instanceof ApplicationError) {
    return false;
  }
});

watch(
  () => auth.authStateVersion,
  () => {
    // Pending confirmations and contacts belong to the outgoing patient session.
    DialogService.close(false);
    CallSheetService.reset();
  },
  { flush: 'sync' },
);
watch(
  () => auth.account.mainUnique,
  () => {
    preview.$reset();
    dryWeightUpdates.$reset();
  },
);
onMounted(async () => {
  if (Capacitor.getPlatform() !== 'android') {
    return;
  }
  backListener = await NativeApp.addListener('backButton', ({ canGoBack }) => {
    if (canGoBack) {
      router.back();
    } else if (route.path !== '/home' && auth.isLoggedIn) {
      void router.replace('/home');
    } else {
      void NativeApp.minimizeApp();
    }
  });
  if (!mounted) {
    await backListener.remove();
  }
});
onBeforeUnmount(() => {
  mounted = false;
  void backListener?.remove();
});
</script>
<style scoped>
.app-progress {
  z-index: 100;
  width: min(100%, var(--app-frame-width));
  transform-origin: left;
  animation: loading 1s ease-in-out infinite alternate;
}
@keyframes loading {
  from {
    transform: scaleX(0.15);
  }
  to {
    transform: scaleX(1);
  }
}
</style>
